/**
 * @file CityRolePage.jsx
 * @description Data-driven role×city SEO landing page (e.g. /rbt-jobs-miami-fl).
 *   Renders genuinely localized, crawlable content from src/data/seoLanding.js —
 *   nearby areas, typical schedules, case types, market pay context, and
 *   provider pain points — so each page is substantive, not a thin city-name
 *   swap. Honest about volume: no fake listings, no claim of a cadence of new
 *   cases. The empty-state line ("No open cases in [Area] right now?") shows
 *   only when a live check says there are none; the primary CTA is "Get
 *   [Area] case alerts". "Open the Case Finder" appears only when the Admin
 *   setting `public_case_browsing` is ON (otherwise guests would hit a signup
 *   form); with it OFF the secondary CTA is the free tracker. Emits FAQPage
 *   JSON-LD and links to sibling pages + the core landings for crawl discovery.
 *
 *   Rendered under TopNav (guest nav), so it has no brand header of its own.
 * @module components/CityRolePage
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  MapPinIcon, DollarIcon, CalendarIcon, ShieldIcon, BriefcaseIcon, UsersIcon,
  XIcon, BellIcon, GlobeIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import QuietEnding from "./QuietEnding";
import { CasePreview } from "./AgencyVisuals";
import { MoreDetails, FaqDetails } from "./Disclosure";
import StickyMobileCta from "./StickyMobileCta";
import CityHeroChips from "./cityRole/CityHeroChips";
import CityNoRbtYet from "./cityRole/CityNoRbtYet";
import CityAtAGlance, { hasGlance } from "./cityRole/CityAtAGlance";
import { setSignupIntent } from "../lib/signupIntent";
import { useFaqJsonLd, useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A } from "../lib/analytics";
import { getLanding, relatedLandings, AREAS, STATES } from "../data/seoLanding";
import { usePublicCaseBrowsing } from "../lib/publicBrowsing";
import { hasPublicOpenCasesNear } from "../lib/caseService";
import { seoCaseTypesForRole } from "../lib/findCasesQuery";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

/**
 * Build the city-specific FAQ (honest about pre-launch volume).
 * i18n: every Q/A is resolved via t() — `d` carries the language-aware display
 * values (roleSingular/rolePlural/area/loc/region) computed in the component.
 */
function buildFaqs(t, entry, d, hasLive) {
  return [
    {
      q: t("cityRole.faq1Q", "Do I need a car to take {{role}} cases in {{area}}?", { role: d.roleSingular, area: d.area }),
      a: t("cityRole.faq1A", "Many ABA cases in {{region}} are in-home or school-based, so reliable transportation helps. ABAcases shows the location and approximate commute on every case up front, so you can filter by distance before you ever apply, no surprise drives across {{area}}.", { region: d.region, area: d.area }),
    },
    {
      q: t("cityRole.faq2Q", "How much do {{rolePlural}} earn in {{loc}}?", { role: d.roleSingular, rolePlural: d.rolePlural, loc: d.loc }),
      a: t(entry.payDetailKey),
    },
    // The area's own sourced questions for this role (seoAreas.json faqByRole), e.g. Spanish and RBT certification in Miami.
    ...(entry.extraFaqs || []).map((f) => ({ q: t(f.q), a: t(f.a) })),
    // People search "how to become a BCBA in <state>" (Search Console, 2026-10-21). State pages and BCBA/BCaBA pages
    // answer it: the national BACB path, then the state's sourced licensing fact (seoLanding licensingKey).
    ...(entry.licensingKey && (entry.roleKey === "bcba" || entry.roleKey === "bcaba" || entry.areaType === "state") ? [{
      q: t("cityRole.faqBcbaPathQ", "How do I become a BCBA in {{state}}?", { state: d.stateName }),
      a: `${t("cityRole.faqBcbaPathA")} ${t(entry.licensingKey)}`,
    }] : []),
    {
      // Asked as "right now?" only when the live check can answer yes; otherwise the honest question is how to catch
      // one, and the answer says cases change day to day (persona round 5: a "right now?" with no yes read as a dodge).
      q: hasLive === true
        ? t("cityRole.faq3Q", "Are there {{role}} cases available in {{area}} right now?", { role: d.roleSingular, area: d.area })
        : t("cityRole.faq3QCatch", "How do I catch {{role}} cases in {{area}}?", { role: d.roleSingular, area: d.area }),
      a: hasLive === true
        ? (entry.areaType === "state" || entry.areaType === "region"
          ? t("cityRole.faq3AHasBroad", "Yes. Cases are open in {{area}} now. Create a free profile to see them and get an alert when a new one opens.", { area: d.area })
          : t("cityRole.faq3AHas", "Yes. Cases are open near {{area}} now. Create a free profile to see them and get an alert when a new one opens.", { area: d.area }))
        : entry.roleKey === "bcba" || entry.roleKey === "bcaba"
          ? t("cityRole.faq3AStaff", "Cases change day to day. Create a free profile, keep alerts on and check your email and messages.")
          : t("cityRole.faq3A", "Cases change day to day and can fill fast. Create a free profile, keep alerts on and check your email and messages."),
    },
    {
      q: t("therapistLanding.faq10Q"),
      a: t("therapistLanding.faq10A"),
    },
    {
      q: t("cityRole.faq4Q", "Is ABAcases free for {{role}}?", { role: d.rolePlural }),
      a: t("cityRole.faq4A", "Yes. Providers do not pay to browse, apply, get case alerts, or use the ABA timesheet & payment tracker. We charge agencies, not the people doing the work."),
    },
  ];
}

// English plurals as a real noun, never "role + s": the aba-cases role has no
// natural singular noun ("ABA"), so "{{role}}s" rendered the bug "ABAs".
const ROLE_PLURAL_EN = {
  rbt: "RBTs",
  bt: "behavior technicians",
  bcaba: "BCaBAs",
  bcba: "BCBAs",
  "aba-cases": "ABA providers",
};

// Spanish role words don't follow the English "just append s" pluralization —
// singular vs. plural need distinct, grammatically correct forms (RBT/BCBA are
// invariant; the aba-cases role needs real Spanish nouns +
// plurals when embedded in a sentence — this describes the PERSON ("an ABA
// case provider"), unrelated to the ABAcases brand name (see ROLE_LABEL_ES
// below for the brand-name badge, which is a separate concern).
// Credential names and acronyms (RBT, BCBA, BT, BCaBA) stay unchanged per the
// house style guide — only the surrounding role word translates.
const ROLE_WORD_ES = {
  rbt: "RBT",
  bt: "técnico de conducta (BT)",
  bcaba: "BCaBA",
  bcba: "BCBA",
  "aba-cases": "proveedor ABA",
};
const ROLE_WORD_ES_PLURAL = {
  rbt: "RBT",
  bt: "técnicos de conducta",
  bcaba: "BCaBA",
  bcba: "BCBA",
  "aba-cases": "proveedores ABA",
};
// The small role-tag badge shows entry.role.label verbatim (RBT/BCBA/
// ABAcases) — a category tag, not a sentence, so it needs its own
// map: "aba-cases" here is the literal label "ABAcases" (the brand name,
// left untranslated like the RBT/BCBA acronyms), NOT the "ABA"/"proveedor
// ABA" substitution used when the role is embedded in a sentence
// (roleSingular/rolePlural above, which describe the person, not the brand).
const ROLE_LABEL_ES = {
  rbt: "RBT",
  bt: "BT",
  bcaba: "BCaBA",
  bcba: "BCBA",
  "aba-cases": "ABAcases",
};

// Place names that differ in Spanish come from the registry (seoAreas.json `nameEs`, e.g. "Carolina del Norte");
// city names (Miami, Charlotte...) are proper nouns and stay the same in both languages.
const nameEsOf = (entry) => (entry && entry.nameEs) || null;

export default function CityRolePage({ slug, onNavigate }) {
  const { t, i18n } = useTranslation();
  const entry = getLanding(slug);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Admin `public_case_browsing`: ON = guests may open the Case Finder; OFF = the
  // finder would just show them a signup gate, so we don't point at it.
  const browsingOn = usePublicCaseBrowsing();

  // Do live public cases exist for this area? null = unknown (prerender, or the
  // query failed). Drives the empty-state line so it never claims "no open
  // cases" unless we actually checked.
  const [hasLive, setHasLive] = useState(null);
  useEffect(() => {
    if (!entry) return undefined;
    let cancelled = false;
    const cities = entry.areaType === "state" ? [] : [entry.area, ...entry.nearby];
    // By role: one RBT case nearby must not make the BCBA page say BCBA cases are open (David, 2026-10-20).
    hasPublicOpenCasesNear({ state: entry.state, cities, types: seoCaseTypesForRole(entry.roleKey) }).then((r) => { if (!cancelled) setHasLive(r); }).catch(() => {});
    return () => { cancelled = true; };
  }, [entry]);

  // English role word — used ONLY for JSON-LD breadcrumb schema, which (like
  // metaTitle/metaDescription/canonical below) is intentionally English-locked;
  // the raw prerendered SEO surface doesn't change with the language selector.
  // The role as people search it: "Behavior Technician (BT)" rather than "BT" (ROLES.wordEn), else its label.
  const roleWord = entry ? (entry.roleKey === "aba-cases" ? "ABA" : (entry.role.wordEn || entry.role.label)) : "ABA";

  // Language-aware display values for the VISIBLE page body (h1, subtitle, FAQ,
  // headings, CTAs, etc.). City/county names (Miami, Broward, Charlotte...) are
  // proper nouns and stay identical in both languages; only the two hub names
  // (South Florida / North Carolina) and the role word actually swap.
  const isEs = !!entry && i18n.language?.startsWith("es");
  const d = entry ? {
    // In the body a BT is just "BT" (persona review: "Behavior Technician (BT)" six times read like a robot); the
    // H1 and title keep the full search phrase.
    roleSingular: isEs ? (ROLE_WORD_ES[entry.roleKey] || entry.role.label) : (entry.roleKey === "bt" ? entry.role.label : roleWord),
    rolePlural: isEs ? (ROLE_WORD_ES_PLURAL[entry.roleKey] || entry.role.label) : (ROLE_PLURAL_EN[entry.roleKey] || `${roleWord}s`),
    // The literal role LABEL (RBT/BCBA/ABAcases) for the small
    // category badge — distinct from roleSingular, which substitutes "ABA" for
    // the aba-cases role when embedded in a sentence.
    roleLabel: isEs ? (ROLE_LABEL_ES[entry.roleKey] || entry.role.label) : entry.role.label,
    area: isEs ? (nameEsOf(entry) || entry.area) : entry.area,
    // locLabel already carries the ", ST" suffix for city/county pages and is
    // bare for state/region hubs (locLabel === area) — swap only the latter.
    loc: isEs && entry.locLabel === entry.area ? (nameEsOf(entry) || entry.area) : entry.locLabel,
    region: isEs && entry.region === entry.area ? (nameEsOf(entry) || entry.area) : entry.region,
    stateName: (isEs && STATES[entry.state] && STATES[entry.state].nameEs) || (STATES[entry.state] && STATES[entry.state].name) || entry.stateFull,
  } : null;

  const faqs = entry ? buildFaqs(t, entry, d, hasLive) : [];
  // Internal-link mesh: sibling landing pages (same city/other roles + same-role
  // hub family), rendered as crawlable <a> anchors so no landing page is orphaned.
  const related = entry ? relatedLandings(slug) : [];

  usePageSeo(entry ? {
    title: entry.metaTitle,
    description: entry.metaDescription,
    canonical: entry.canonical,
  } : {});
  useSiteSchema(); // global Organization + WebSite
  useFaqJsonLd(`faq-jsonld-${slug}`, faqs);
  useBreadcrumbJsonLd(`bc-jsonld-${slug}`, entry ? [
    { name: "Home", url: "/" },
    // NC city pages point up to the North Carolina hub; others to the Case Finder.
    entry.parentHub
      ? { name: entry.parentHub.name, url: `/${entry.parentHub.slug}` }
      : { name: "ABA Case Finder", url: "/aba-case-finder" },
    { name: `${roleWord} cases in ${entry.area}`, url: entry.canonical },
  ] : []);

  if (!entry) return null;

  const goAlerts = () => { track(A.CITY_PAGE_CASE_ALERT_CLICKED, { supabaseMeta: { slug } }); setSignupIntent("alerts"); onNavigate("auth"); };
  const goFinder = () => { onNavigate("cases-v2"); };
    const isBroad = entry.areaType === "state" || entry.areaType === "region";
  // One short line under the CTA (persona review 2026-10-20: "no cases yet" read as small print far below the
  // button). Says "cases are open" only when a live check found some; a new place says it is new; otherwise nothing.
  // Follows the live check (cases open while a page is live). When none is found the page never says "no cases":
  // cases pop up without warning and can fill the same day, so the message is sign up, get alerts, keep checking
  // (owner 2026-10-20).
  const statusText = hasLive === true
    ? (isBroad
      ? t("cityRole.statusHasBroad", "Cases are open in {{area}} now.", { area: d.area })
      : t("cityRole.statusHas", "Cases are open near {{area}} now.", { area: d.area }))
    // Analyst staff roles are hired through interviews, so "same day" would read as pressure there (persona round 5).
    : entry.roleKey === "bcba" || entry.roleKey === "bcaba"
      ? (isBroad
        ? t("cityRole.statusAlertBroad", "Get an alert the moment a {{role}} case opens in {{area}}.", { role: d.roleSingular, area: d.area })
        : t("cityRole.statusAlert", "Get an alert the moment a {{role}} case opens near {{area}}.", { role: d.roleSingular, area: d.area }))
      : t("cityRole.statusFlow", "Cases pop up fast and can fill the same day.");

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  // The "at a glance" box shows only with a sourced pay line, and then carries it, so the pay card would repeat it.
  const glanceHasPay = hasGlance(entry);
  const maxW = 980;

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs items={[
            { label: t("cityRole.breadcrumbHome", "Home"), onClick: () => onNavigate("home") },
            entry.parentHub
              ? { label: isEs ? (AREAS[entry.parentHub.slug.replace(/^rbt-jobs-/, "")]?.nameEs || entry.parentHub.name) : entry.parentHub.name, onClick: () => onNavigate(entry.parentHub.slug) }
              : { label: t("cityRole.breadcrumbFinder", "ABA Case Finder"), onClick: () => onNavigate("aba-case-finder") },
            { label: `${d.roleSingular} · ${d.area}` },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: T.indigoDark, background: "rgba(255,255,255,0.92)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{d.roleLabel}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>{d.loc}</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {entry.roleKey === "aba-cases"
              ? t("cityRole.h1Cases", "ABA Cases in {{loc}}", { loc: d.loc })
              : t("cityRole.h1Role", "{{role}} Jobs in {{loc}}", { role: isEs ? d.roleSingular : roleWord, loc: d.loc })}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 26px" }}>
            {t("cityRole.subtitleShort", "Cases that fit your schedule, commute and pay.")}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={goAlerts} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: T.gradientIndigo, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
              <BellIcon size={16} color="#fff" />{" "}
              {entry.roleKey === "aba-cases"
                ? t("cityRole.ctaAlertCases", "Get {{area}} ABA Case Alerts", { area: d.area })
                : t("cityRole.ctaAlertRole", "Get {{area}} {{role}} Case Alerts", { area: d.area, role: d.roleSingular })}
            </button>
          </div>
          {statusText && <p data-testid="city-status" style={{ fontSize: 15, color: "#cbd5e1", margin: "12px 0 0", fontWeight: 500 }}>{statusText}</p>}
          <CityHeroChips roleKey={entry.roleKey} onNavigate={onNavigate} isMobile={isMobile} />
        </div>
      </header>

      {entry.roleKey === "bt" && <CityNoRbtYet onNavigate={onNavigate} isMobile={isMobile} state={STATES[entry.state]} />}

      {/* ── Launch note: what ABAcases covers in this place (never "no cases yet"). ── */}
      {entry.launchNoteKey && (
        <div style={{ background: "#eef2ff", borderBottom: `1px solid ${BORDER}`, padding: isMobile ? "18px 18px" : "22px 48px" }}>
          <div style={{ maxWidth: maxW, margin: "0 auto", display: "flex", alignItems: "flex-start", gap: 12 }}>
            <BriefcaseIcon size={18} color={INDIGO} style={{ flexShrink: 0, marginTop: 3 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: INDIGO, marginBottom: 5 }}>
                {t(entry.launchTitleKey)}
              </div>
              <MoreDetails text={t(entry.launchNoteKey)} max={170} style={{ fontSize: isMobile ? 14 : 14.5, color: NAVY, lineHeight: 1.65, fontWeight: 500 }} />
            </div>
          </div>
        </div>
      )}

      {/* ── At a glance: short, dated, sourced facts (only where the area has them) ── */}
      <CityAtAGlance entry={entry} d={d} isMobile={isMobile} isEs={isEs} />

      {/* ── Sample case (labelled "Sample"): what a provider sees before applying ── */}
      <section data-testid="city-sample-case" style={{ padding: isMobile ? "36px 18px 0" : "48px 48px 0" }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 21 : 26, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em", textAlign: "center" }}>{t("cityRole.sampleTitle")}</h2>
          <CasePreview variant="provider" isMobile={isMobile} area={isBroad ? entry.nearby[0] : d.area} role={entry.roleKey === "aba-cases" && !isEs ? "ABA provider" : d.roleSingular} staff={entry.roleKey === "bcba" || entry.roleKey === "bcaba"} roleKey={entry.roleKey} />
        </div>
      </section>

      {/* ── Why ABAcases for [role] in [area] ───────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 26px", letterSpacing: "-0.02em" }}>
            {t("cityRole.whyTitle", "Why {{role}} use ABAcases in {{area}}", { role: d.rolePlural, area: d.area })}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : `repeat(${glanceHasPay ? 3 : 2}, 1fr)`, gap: 16 }}>
            {[
              {
                id: "mapped", Icon: MapPinIcon,
                title: t("cityRole.cardMappedTitle", "Local {{role}} cases, mapped", { role: d.roleSingular }),
                // The merged /aba-therapist-jobs-* pages 301 here, so the aba-cases pages carry the "ABA therapist jobs" search intent once, in body copy.
                body: entry.roleKey === "aba-cases"
                  ? t("cityRole.cardMappedBodyCases", "Looking for ABA therapist jobs in {{area}}? Browse ABA cases across {{area}} and nearby {{nearby}} on a map, filtered by commute radius, not a keyword search.", { area: d.area, nearby: entry.nearby.slice(0, 4).join(", ") })
                  : t("cityRole.cardMappedBody", "See {{role}} cases across {{area}} and nearby {{nearby}} on a map, filtered by commute radius, not a keyword search.", { role: d.roleSingular, area: d.area, nearby: entry.nearby.slice(0, 4).join(", ") }),
              },
              // The at a glance box already shows this pay line, so the card would repeat it.
              ...(glanceHasPay ? [] : [{
                id: "pay", Icon: DollarIcon,
                title: t("cityRole.cardPayTitle", "Typical pay here"),
                body: t(entry.paySummaryKey),
              }]),
              {
                id: "schedule", Icon: CalendarIcon,
                title: t("cityRole.cardScheduleTitle", "Schedules that fit"),
                body: t("cityRole.cardScheduleBody", "Common {{area}} schedules include {{schedules}}. Filter to the hours you can actually work.", { area: d.area, schedules: entry.role.scheduleKeys.map((k) => t(k)).join(", ") }),
              },
              {
                id: "credential", Icon: ShieldIcon,
                title: t("cityRole.cardCredentialTitle", "Credential-matched"),
                body: t("cityRole.cardCredentialBody", "Cases are matched to your {{credential}} and skills, so you only see {{role}} work you're qualified for.", { credential: t(entry.role.credentialKey), role: d.roleSingular }),
              },
            ].map((c) => (
              <div key={c.id} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={19} color={INDIGO} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 5 }}>{c.title}</div>
                  <MoreDetails text={c.body} style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.6 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pain points (why this exists) ────────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t("cityRole.tiredOfTitle", "What {{role}} in {{area}} are tired of", { role: d.rolePlural, area: d.area })}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            {entry.painPointKeys.map((key) => (
              <div key={key} style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "14px 16px" }}>
                <XIcon size={15} color={T.red} style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.55 }}>{t(key)}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 14.5, color: NAVY, fontWeight: 600, marginTop: 20, lineHeight: 1.6 }}>
            {t("cityRole.painClosing", "ABAcases fixes both sides: find {{role}} cases that actually fit in {{area}}, then track every hour and every dollar you're owed with the free Pay & Hours tracker.", { role: d.roleSingular, area: d.area })}
          </p>
          {entry.roleKey === "bcba" && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "#eef2ff", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "14px 16px", marginTop: 18 }}>
              <MapPinIcon size={16} color={INDIGO} style={{ flexShrink: 0, marginTop: 2 }} />
              <span style={{ fontSize: 14, color: NAVY, fontWeight: 600, lineHeight: 1.6 }}>
                {t("cityRole.bcbaRemote", "Open to remote supervision? Turn on remote work in your profile to see BCBA cases from other states you are licensed in.")}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* ── Areas served ─────────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 21 : 26, fontWeight: 800, color: NAVY, margin: "0 0 8px", letterSpacing: "-0.02em" }}>
            {isBroad
              ? t("cityRole.nearTitleBroad", "{{role}} cases across {{area}}", { role: d.roleSingular, area: d.area })
              : t("cityRole.nearTitle", "{{role}} cases near {{area}}", { role: d.roleSingular, area: d.area })}
          </h2>
          <p style={{ fontSize: 14, color: TEXT_MID, marginBottom: 18 }}>
            {isBroad
              ? t("cityRole.weCoverBroad", "Cities across {{region}}:", { region: d.region })
              : t("cityRole.weCover", "We cover {{region}} and surrounding communities:", { region: d.region })}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
            {entry.nearby.map((n) => (
              <span key={n} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: TEXT_MID, background: T.surfaceAlt, border: `1px solid ${BORDER}`, borderRadius: 99, padding: "8px 14px" }}>
                <MapPinIcon size={12} color={INDIGO} /> {n}
              </span>
            ))}
          </div>
          {browsingOn && (
            <div style={{ marginTop: 20 }}>
              <button onClick={goFinder} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: INDIGO, fontSize: 14.5, fontWeight: 700, textDecoration: "underline", fontFamily: "inherit" }}>
                {t("providerPages.openCaseFinder", "Open the Case Finder")} →
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── Local picture (per-metro genuine facts: commute, schools, demand) ── */}
      {entry.localFactKeys && (
        <section style={{ padding: sectionPad }}>
          <div style={{ maxWidth: maxW, margin: "0 auto" }}>
            <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
              {t("cityRole.localTitle", "{{role}} work in {{area}}: the local picture", { role: d.roleSingular, area: d.area })}
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
              {[
                // State pages (page engine, 2026-10-20) lead with what genuinely differs by state: licensing, who
                // funds ABA, and the workforce (public BACB data). City pages keep commute, schools and demand.
                { label: t("cityRole.localLicensingLabel", "Licensing here"), Icon: ShieldIcon, k: entry.localFactKeys.licensing },
                { label: t("cityRole.localFundingLabel", "Who funds ABA here"), Icon: DollarIcon, k: entry.localFactKeys.funding },
                { label: t("cityRole.localWorkforceLabel", "The ABA workforce"), Icon: UsersIcon, k: entry.localFactKeys.workforce },
                { label: t("cityRole.localCommuteLabel", "Getting around"), Icon: MapPinIcon, k: entry.localFactKeys.commute },
                { label: t("cityRole.localSchoolLabel", "School-based cases"), Icon: CalendarIcon, k: entry.localFactKeys.school },
                { label: t("cityRole.localDemandLabel", "Local demand"), Icon: BriefcaseIcon, k: entry.localFactKeys.demand },
                // Census language shares sat under "Local demand" and read as a mislabel (persona reviews, 2026-10-20).
                { label: t("cityRole.localLanguagesLabel", "Languages spoken"), Icon: GlobeIcon, k: entry.localFactKeys.languages },
              ].filter((x) => x.k).map((x, i) => (
                <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "18px 20px" }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <x.Icon size={18} color={INDIGO} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: NAVY, marginBottom: 5 }}>{x.label}</div>
                    <MoreDetails text={t(x.k)} style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.65 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Related pages (internal-link mesh / hub-and-spoke) ─────────────── */}
      {related.length > 0 && (
        <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, padding: isMobile ? "36px 18px" : "48px 48px" }}>
          <div style={{ maxWidth: maxW, margin: "0 auto" }}>
            <h2 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: NAVY, margin: "0 0 14px", letterSpacing: "-0.02em" }}>
              {t("cityRole.relatedTitle", "More ABA case pages")}
            </h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
              {related.map((r) => {
                // Same house-style rules as d.loc / d.roleLabel above: city and
                // county names are proper nouns and stay as-is; only the two
                // hub names (South Florida / North Carolina) swap in ES, and the
                // role label uses the ES map (RBT/BCBA invariant). The connector ("in" → "en") lives in the key.
                const rLoc = isEs && r.locLabel === r.area ? (r.nameEs || r.locLabel) : r.locLabel;
                const rRole = isEs ? (ROLE_LABEL_ES[r.roleKey] || r.role.label) : r.role.label;
                const rLabel = r.roleKey === "aba-cases"
                  ? t("cityRole.relatedLinkCases", "ABA cases in {{loc}}", { loc: rLoc })
                  : t("cityRole.relatedLinkRole", "{{role}} in {{loc}}", { role: rRole, loc: rLoc });
                return (
                  <a
                    key={r.slug}
                    href={seoHref(r.slug)}
                    onClick={(e) => { e.preventDefault(); onNavigate(r.slug); }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: INDIGO, background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 99, padding: "8px 14px", textDecoration: "none" }}
                  >
                    <MapPinIcon size={12} color={INDIGO} />{" "}
                    {rLabel}
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Free tracker mention (bonus feature, no CTA — not every visitor
              cares about the tracker, so this stays a low-key mention rather
              than a dedicated CTA section/button) ────────────────────────── */}
      <div style={{ padding: isMobile ? "0 18px 44px" : "0 48px 60px" }}>
        <div style={{ maxWidth: maxW, margin: "0 auto", display: "flex", alignItems: "flex-start", gap: 14, background: T.surfaceAlt, border: `1px solid ${BORDER}`, borderRadius: 14, padding: isMobile ? "18px 18px" : "20px 24px" }}>
          <DollarIcon size={20} color={INDIGO} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: INDIGO, marginBottom: 5 }}>
              {t("cityRole.trackerEyebrow", "Bonus feature · free")}
            </div>
            <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.65, margin: 0 }}>
              {t("cityRole.trackerMention", "Every free ABAcases profile also includes an ABA timesheet & payment tracker: log hours, catch late payments, and export tax-ready reports across every agency you work with.")}
            </p>
          </div>
        </div>
      </div>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 24px", letterSpacing: "-0.02em", textAlign: "center" }}>
            {t("cityRole.faqTitle", "{{role}} cases in {{area}}: FAQ", { role: d.roleSingular, area: d.area })}
          </h2>
          {/* Same "+" rows as the agency FAQ. <details> keeps every answer in the HTML (and the FAQ JSON-LD is unchanged). */}
          {faqs.map((f, i) => <FaqDetails key={i} q={f.q} a={f.a} isMobile={isMobile} />)}
        </div>
      </section>

      {/* One quiet ending instead of a second CTA band: the hero is this page's only call to action. */}
      <QuietEnding isMobile={isMobile} />

      <PublicFooter />
      {/* Config-aware like the hero: browse CTA when public browsing is on, otherwise alerts. */}
      {browsingOn
        ? <StickyMobileCta label={t("providerPages.openCaseFinder", "Open the Case Finder")} onClick={goFinder} ctaId="city-browse" />
        : <StickyMobileCta label={t("providerPages.getCaseAlerts", "Get case alerts")} onClick={goAlerts} ctaId="city-alerts" />}
    </div>
  );
}
